/**
 * WEB-05 cart rules (pure). One seller per order in the MVP (PRD C-04): adding
 * a unit from another seller is refused with SELLER_CONFLICT so the UI can offer
 * a separate checkout. A refurbished unit is unique (quantity 1); an item sold by
 * quantity (stock_mode 'quantity', D60) carries qty 1..max_qty on its line.
 * Prices are GST-inclusive integer paise.
 */

export const GST_RATE_PCT = 18;
export const EXPRESS_SHIPPING_PAISE = 19900;
export const QUOTE_TTL_MS = 15 * 60 * 1000;

export const emptyCart = () => ({ seller: null, items: [] });

/** The snapshot of a listing a cart line keeps (price is re-quoted at checkout). */
export function cartLine(listing) {
  return {
    listing_id: listing.id,
    // The chosen variant (MERCH-87, lib/variants asVariant); none for a listing without options.
    variant_id: listing.variant_id != null ? listing.variant_id : null,
    variant_label: listing.variant_label || null,
    unit_id: listing.unit_id,
    title: listing.title,
    brand: listing.brand,
    series: listing.series,
    model_name: listing.model_name,
    grade: listing.grade,
    condition: listing.condition,
    condition_label: listing.condition_label,
    condition_badge: listing.condition_badge,
    grade_applies: listing.grade_applies,
    battery_applies: listing.battery_applies,
    inspection_applies: listing.inspection_applies,
    serial_masked: listing.serial_masked,
    battery_health_pct: listing.battery_health_pct,
    warranty_months: listing.warranty_months,
    cpu_family: listing.cpu_family,
    cpu_generation: listing.cpu_generation,
    ram_gb: listing.ram_gb,
    storage_gb: listing.storage_gb,
    storage_type: listing.storage_type,
    display_size: listing.display_size,
    price_paise: listing.price_paise,
    mrp_paise: listing.mrp_paise,
    stock_mode: listing.stock_mode || 'unit',
    qty: 1,
  };
}

/** Sold by quantity (a stock lot) rather than as one unique unit. */
export const isQtyItem = (x) => Boolean(x) && x.stock_mode === 'quantity';
/** A line's quantity (1 for units and lines from before quantity stock). */
export const qtyOf = (i) => Math.max(1, parseInt(i && i.qty, 10) || 1);
/** How many a shopper may choose for a listing or line: up to 10, within what is in stock. */
export const maxQtyOf = (x) => (isQtyItem(x) ? Math.max(0, Math.min(10, Number(x.max_qty != null ? x.max_qty : x.qty_available) || 0)) : 1);

/**
 * Add one unit. Returns { ok: true, cart, already } or
 * { ok: false, error: 'SELLER_CONFLICT', message } when the cart holds another seller's units.
 */
export function addToCart(cart, line, seller) {
  const c = cart && cart.items ? cart : emptyCart();
  if (c.items.length && c.seller && seller && c.seller.slug !== seller.slug) {
    return {
      ok: false,
      error: 'SELLER_CONFLICT',
      message: `Your cart has items from ${c.seller.name}. Each order ships from one seller, so check those out first or start a separate order.`,
    };
  }
  if (c.items.some((i) => sameLine(i, line.listing_id, line.variant_id))) return { ok: true, cart: c, already: true };
  return { ok: true, cart: { seller: c.seller || seller || null, items: [...c.items, line] }, already: false };
}

/** A line is the listing's, and the variant's when one is named (MERCH-87: each variant is its own line). */
export const sameLine = (i, listingId, variantId) => i.listing_id === listingId && (variantId == null || i.variant_id == null || Number(i.variant_id) === Number(variantId));

export function removeFromCart(cart, listingId, variantId = null) {
  const items = (cart.items || []).filter((i) => !sameLine(i, listingId, variantId));
  return { seller: items.length ? cart.seller : null, items };
}

/** Coupon catalogue (fixture until the promotions backend exists). */
export const COUPONS = {
  WELCOME500: { code: 'WELCOME500', label: '₹500 off your first order', kind: 'flat', amount_paise: 50000, min_paise: 2000000 },
  SAVE5: { code: 'SAVE5', label: '5% off, up to ₹2,000', kind: 'pct', pct: 5, cap_paise: 200000, min_paise: 2500000 },
};

/** → { ok, code, discount_paise, label } or { ok:false, error, message }. */
export function applyCoupon(code, subtotalPaise) {
  const key = String(code || '').trim().toUpperCase();
  const c = COUPONS[key];
  if (!c) return { ok: false, error: 'COUPON_INVALID', message: 'That coupon code is not valid.' };
  if (subtotalPaise < c.min_paise) {
    return { ok: false, error: 'COUPON_MIN_ORDER', message: `${c.code} needs an order of at least ₹${(c.min_paise / 100).toLocaleString('en-IN')}.` };
  }
  const discount = c.kind === 'flat' ? c.amount_paise : Math.min(Math.round((subtotalPaise * c.pct) / 100), c.cap_paise);
  return { ok: true, code: c.code, label: c.label, discount_paise: Math.min(discount, subtotalPaise) };
}

/** GST already inside a GST-inclusive amount. */
export function gstIncluded(amountPaise, ratePct = GST_RATE_PCT) {
  return Math.round((amountPaise * ratePct) / (100 + ratePct));
}

/**
 * Price breakdown for the order summary.
 * → { count, mrp_paise, subtotal_paise, item_savings_paise, coupon_paise, shipping_paise, total_paise, gst_included_paise, you_save_paise }
 */
export function cartTotals(items, { shippingPaise = 0, couponPaise = 0 } = {}) {
  const list = items || [];
  const subtotal = list.reduce((s, i) => s + (i.price_paise || 0) * qtyOf(i), 0);
  const mrp = list.reduce((s, i) => s + Math.max(i.mrp_paise || 0, i.price_paise || 0) * qtyOf(i), 0);
  const coupon = Math.min(Math.max(0, couponPaise), subtotal);
  const goods = subtotal - coupon;
  const shipping = list.length ? Math.max(0, shippingPaise) : 0;
  return {
    count: list.reduce((s, i) => s + qtyOf(i), 0),
    mrp_paise: mrp,
    subtotal_paise: subtotal,
    item_savings_paise: mrp - subtotal,
    coupon_paise: coupon,
    shipping_paise: shipping,
    total_paise: goods + shipping,
    gst_included_paise: gstIncluded(goods),
    you_save_paise: mrp - subtotal + coupon,
  };
}

export function isQuoteExpired(quote, now = Date.now()) {
  return !quote || !quote.expires_at || new Date(quote.expires_at).getTime() <= now;
}

export const isValidPin = (pin) => /^[1-9][0-9]{5}$/.test(String(pin || '').trim());
export const isValidPhone = (phone) => /^[6-9][0-9]{9}$/.test(String(phone || '').replace(/\D/g, '').slice(-10));
